import { toCamelOrSnakeCase } from '@/shared/lib/toCamelOrSnakeCase';
import { TokenType, UserInfoType } from '../types';
import { AuthStateStore, authStateStore } from './authState';
import { authSyncStore, AuthSyncStore } from './authSync';
import pkceChallenge from "pkce-challenge";
import { baseService } from '@/shared/api/api';
import { saveReturnUrl } from './returnUrl';

// ---------- Константы ----------
const KEYCLOAK_BASE = process.env.NEXT_PUBLIC_KEYCLOAK_BASE || '';
const CLIENT_ID = process.env.NEXT_PUBLIC_CLIENT_ID || '';

class AuthAsync {
  state: AuthStateStore;
  sync: AuthSyncStore;

  constructor(
    storeState: AuthStateStore,
    syncStore: AuthSyncStore,
  ) {
    this.state = storeState;
    this.sync = syncStore;
  }

  // ---------- Получение токенов по коду ----------
  async fetchGetJWTTokens(code: string, redirectUri: string): Promise<TokenType> {
    const codeVerifier = sessionStorage.getItem("pkce_code_verifier") || "";

    const response = await fetch(`${KEYCLOAK_BASE}/token`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        grant_type: "authorization_code",
        client_id: CLIENT_ID,
        code,
        redirect_uri: `${redirectUri}/empty`,
        code_verifier: codeVerifier,
      }),
    });

    if (!response.ok) throw new Error("Не удалось получить токены");
    return response.json();
  }

  // ---------- Обновление по refresh_token ----------
  async fetchRefreshToken(refreshToken: string): Promise<TokenType> {
    const response = await fetch(`${KEYCLOAK_BASE}/token`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        grant_type: "refresh_token",
        client_id: CLIENT_ID,
        refresh_token: refreshToken,
      })
    });

    if (!response.ok) throw new Error("Не удалось обновить токен");
    return response.json();
  }

  // ---------- Получение userinfo ----------
  async fetchUserInfo(accessToken: string) {
    const response = await baseService.get(`/user/get-user`, {
      method: "GET",
      headers: {
        "Authorization": `Bearer ${accessToken}`
      }
    });
    
    const userData = toCamelOrSnakeCase<UserInfoType>(response.data, true);
    this.sync.setUserInfo(userData);
    return 200;
  }
  async fetchUserInfoWithOutToken() {
    const response = await baseService.get(`/user/get-user`);
    
    const userData = toCamelOrSnakeCase<UserInfoType>(response.data, true);
    this.sync.setUserInfo(userData);
    return 200;
  }
  //---------- Логин юзера в базе ----------
  async fetchPostUserLogin() {
    await baseService.post(`/user/log-login`);

    return 200;
  }

  // ---------- Редирект на Keycloak ----------
  async redirectToKeycloak(redirectUri: string) {
    saveReturnUrl();
    const { code_verifier, code_challenge } = await pkceChallenge();
    sessionStorage.setItem("pkce_code_verifier", code_verifier);
    const keycloakUrl =
      `${KEYCLOAK_BASE}/auth?` +
    `client_id=${CLIENT_ID}` +
    `&redirect_uri=${redirectUri}/empty` +
    `&response_type=code` +
    `&scope=openid` +
    `&code_challenge=${code_challenge}` +
    `&code_challenge_method=S256`;

    window.location.href = keycloakUrl;
  }

  // ---------- Основной refresh/login ----------
  async refresh(code: string, redirectUri: string) {
    try {
      let tokens: TokenType;
      const refreshToken = sessionStorage.getItem("refreshToken");
      if(refreshToken){
        tokens = await this.fetchRefreshToken(refreshToken);
      }else
        if (code) {
          tokens = await this.fetchGetJWTTokens(code, redirectUri);
        } else {
          if (!refreshToken) {
            this.redirectToKeycloak(redirectUri);
            return;
          }
          tokens = await this.fetchRefreshToken(refreshToken);
        }
      sessionStorage.setItem("idToken", tokens.id_token);
      sessionStorage.setItem("accessToken", tokens.access_token);
      sessionStorage.setItem("refreshToken", tokens.refresh_token);

      return tokens;
    } catch (error) {
      console.error("Ошибка авторизации:", error);
      this.redirectToKeycloak(redirectUri);
      throw error;
    }
  }

  async logout(redirectUri: string) {
    const idToken = sessionStorage.getItem("idToken");
    sessionStorage.removeItem("accessToken");
    sessionStorage.removeItem("refreshToken");
    sessionStorage.removeItem("idToken");
    sessionStorage.removeItem("pkce_code_verifier");

    let logoutUrl = `${KEYCLOAK_BASE}/logout?post_logout_redirect_uri=${encodeURIComponent(redirectUri)}`;
    
    if (idToken) {
      logoutUrl += `&id_token_hint=${idToken}`;
    }
    window.location.href = logoutUrl;
  }
}

export const authAsyncStore = new AuthAsync(
  authStateStore,
  authSyncStore,
);
export type AuthAsyncStore = typeof authAsyncStore;
